import React, { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, FileUp, Loader2, Save, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import CertificateTemplateDesigner from "../components/CertificateTemplateDesigner";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const COPY = {
  fr: {
    back: "Retour",
    badge: "Modèle d’organisation",
    title: "Positionnement du certificat",
    subtitle: "Placez chaque donnée sur le PDF officiel sans modifier le moteur CB4C.",
    loading: "Chargement du modèle...",
    cb4cTitle: "Modèles CB4C protégés",
    cb4cText: "CB4C conserve ses quatre modèles et son moteur de génération actuel.",
    save: "Enregistrer la mise en page",
    saving: "Enregistrement...",
    saved: "Mise en page enregistrée.",
    replace: "Remplacer le PDF",
    replaceHint: "Un nouveau PDF exige une nouvelle validation des positions.",
    choose: "Choisir un nouveau PDF",
    error: "Impossible de charger ou d’enregistrer le modèle.",
    validate: "Validez la mise en page avant l’enregistrement.",
  },
  en: {
    back: "Back",
    badge: "Organization template",
    title: "Certificate positioning",
    subtitle: "Place each value on the official PDF without changing the CB4C engine.",
    loading: "Loading template...",
    cb4cTitle: "Protected CB4C templates",
    cb4cText: "CB4C keeps its four templates and current generation engine.",
    save: "Save layout",
    saving: "Saving...",
    saved: "Layout saved.",
    replace: "Replace PDF",
    replaceHint: "A new PDF requires a new position validation.",
    choose: "Choose a new PDF",
    error: "Unable to load or save the template.",
    validate: "Validate the layout before saving.",
  },
};

const CertificateTemplateSettings = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const copy = COPY[lang === "en" ? "en" : "fr"];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [file, setFile] = useState(null);
  const [replacementFile, setReplacementFile] = useState(null);
  const [layout, setLayout] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const headers = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async ({ preserveMessage = false } = {}) => {
    setLoading(true);
    if (!preserveMessage) setMessage("");
    try {
      const policyResponse = await fetch(
        `${API_URL}/api/organizations/current/template-policy`,
        { headers: headers() }
      );
      const policyData = await policyResponse.json().catch(() => ({}));
      if (!policyResponse.ok) throw new Error(policyData.message || copy.error);

      // Le Super Admin reçoit mode="platform" côté backend.
      // Il est redirigé et ne peut pas utiliser cette page.
      if (policyData?.mode === "platform") {
        navigate("/dashboard", {
          replace: true,
          state: {
            denied: true,
            code: "ORGANIZATION_ADMIN_REQUIRED",
          },
        });
        return;
      }

      setPolicy(policyData);

      if (policyData.mode !== "single" || !policyData.template) return;
      const fileResponse = await fetch(
        `${API_URL}/api/organizations/current/template-file`,
        { headers: headers() }
      );
      if (!fileResponse.ok) throw new Error(copy.error);
      const blob = await fileResponse.blob();
      const loadedFile = new File(
        [blob],
        policyData.template.filename || "certificate-template.pdf",
        { type: "application/pdf" }
      );
      setFile(loadedFile);
      setLayout(policyData.template.layout || null);
    } catch (error) {
      setMessageType("error");
      setMessage(error?.message || copy.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleReplacement = (event) => {
    const nextFile = event.target.files?.[0] || null;
    if (!nextFile) return;
    setReplacementFile(nextFile);
    setFile(nextFile);
    setLayout(null);
    setMessage("");
  };

  const save = async () => {
    if (!layout?.configured) {
      setMessageType("error");
      setMessage(copy.validate);
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      let response;
      if (replacementFile) {
        const payload = new FormData();
        payload.append("certificateTemplate", replacementFile);
        payload.append("templateLayout", JSON.stringify(layout));
        response = await fetch(`${API_URL}/api/organizations/current/template`, {
          method: "PUT",
          headers: headers(),
          body: payload,
        });
      } else {
        response = await fetch(`${API_URL}/api/organizations/current/template-layout`, {
          method: "PATCH",
          headers: {
            ...headers(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ layout }),
        });
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || copy.error);
      setMessageType("success");
      setMessage(copy.saved);
      setReplacementFile(null);
      await load({ preserveMessage: true });
    } catch (error) {
      setMessageType("error");
      setMessage(error?.message || copy.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 shadow-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          {copy.back}
        </button>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl sm:p-8">
          <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700">
                <ShieldCheck className="h-4 w-4" />
                {copy.badge}
              </div>
              <h1 className="mt-4 text-3xl font-black tracking-tight text-slate-950">{copy.title}</h1>
              <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-600">{copy.subtitle}</p>
            </div>

            {policy?.mode === "single" && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-50">
                  <FileUp className="h-4 w-4" />
                  {copy.choose}
                  <input type="file" accept="application/pdf,.pdf" onChange={handleReplacement} className="sr-only" />
                </label>
                <button
                  type="button"
                  disabled={saving || !file}
                  onClick={save}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-black text-white shadow-lg shadow-blue-600/20 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {saving ? copy.saving : copy.save}
                </button>
              </div>
            )}
          </div>

          {message && (
            <div className={`mb-5 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold ${
              messageType === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-rose-200 bg-rose-50 text-rose-700"
            }`}>
              {messageType === "success" && <CheckCircle2 className="h-5 w-5" />}
              {message}
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center gap-3 text-sm font-black text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              {copy.loading}
            </div>
          ) : policy?.mode === "cb4c-four" ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <h2 className="text-lg font-black text-amber-950">{copy.cb4cTitle}</h2>
              <p className="mt-2 text-sm font-semibold text-amber-800">{copy.cb4cText}</p>
            </div>
          ) : file ? (
            <>
              {replacementFile && (
                <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-xs font-bold text-violet-800">
                  {copy.replaceHint}
                </div>
              )}
              <CertificateTemplateDesigner
                file={file}
                initialLayout={layout}
                onChange={setLayout}
                language={lang}
                organizationName={policy?.organization?.name}
              />
            </>
          ) : (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm font-bold text-rose-700">
              {copy.error}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default CertificateTemplateSettings;
